// Özellikler ve varyant: tek özellik sistemi (InventoryProductFeature). Eski 41 sabit alan kaldırıldı.
import React, { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, Stack, TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { ProductFeatureService } from '../../services/productFeatureService';
import type { StockCardForm } from '../useStockCardForm';
import { GroupLabel, SectionTitle } from './fields';

// Sık kullanılan özellikler: şirkette tanımı yoksa tıklayınca oluşturulur.
const SUGGESTED_FEATURES = [
  'Porsiyon', 'Kalori', 'Alerjen', 'Acılık', 'İçindekiler',
  'Renk', 'Beden', 'Malzeme', 'Ağırlık', 'Hacim',
];

const sameName = (a: string, b: string) => a.trim().toLocaleLowerCase('tr-TR') === b.trim().toLocaleLowerCase('tr-TR');

interface VariantLabel { name?: string; value?: string; Name?: string; Value?: string }

export const FeaturesSection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const [busyName, setBusyName] = useState<string | null>(null);
  const [featureError, setFeatureError] = useState<string | null>(null);

  const usedIds = new Set(f.itemFeatureValues.map(r => r.featureId).filter(Boolean));
  const usedNames = new Set(
    f.availableFeatures.filter(def => usedIds.has(def.id)).map(def => def.name.trim().toLocaleLowerCase('tr-TR'))
  );
  const suggestions = SUGGESTED_FEATURES.filter(name => !usedNames.has(name.toLocaleLowerCase('tr-TR')));

  const rawVariants = (f.itemToEdit as any)?.variants ?? (f.itemToEdit as any)?.Variants;
  const variantLabels: VariantLabel[] = Array.isArray(rawVariants) ? rawVariants : [];
  const isVariantChild = Boolean((f.itemToEdit as any)?.parentItemId ?? (f.itemToEdit as any)?.ParentItemId);

  const addRow = (featureId = '') => {
    f.setItemFeatureValues(prev => ([...prev, { tempId: `${Date.now()}-${prev.length}`, featureId, value: '' }]));
  };

  const addSuggested = async (name: string) => {
    setFeatureError(null);
    const existing = f.availableFeatures.find(def => sameName(def.name, name));
    if (existing) {
      addRow(existing.id);
      return;
    }
    setBusyName(name);
    try {
      const created = await ProductFeatureService.create({
        companyId: f.companyId ?? undefined,
        name,
        isActive: true,
        orderIndex: f.availableFeatures.length,
      });
      f.setAvailableFeatures(prev => [...prev, created]);
      addRow(created.id);
    } catch (e: any) {
      setFeatureError(t('stock.addModal.layout.featureCreateFailed', 'Özellik tanımı oluşturulamadı: {{message}}', {
        message: e?.response?.data?.message || e?.message || '',
      }));
    } finally {
      setBusyName(null);
    }
  };

  return (
    <Box>
      <SectionTitle
        title={t('stock.addModal.layout.sections.features', 'Özellikler ve varyant')}
        hint={t('stock.addModal.layout.featuresHint', 'Porsiyon, kalori, renk gibi bilgiler. QR menüde ürün detayında gösterilir.')}
      />

      {f.itemType === 'PRODUCT' && (
        <FormControlLabel
          control={<Checkbox checked={f.isVariantParent} onChange={(e) => f.setIsVariantParent(e.target.checked)} />}
          label={t('stock.addModal.layout.variantParent', 'Bu ürünün çeşitleri var (renk, beden gibi alt ürünlerin ana ürünü)')}
        />
      )}

      {isVariantChild && (
        <Box sx={{ mt: 1 }}>
          <GroupLabel>{t('stock.addModal.layout.variantLabels', 'Varyant bilgisi')}</GroupLabel>
          {variantLabels.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {variantLabels.map((v, i) => (
                <Chip key={i} size="small" label={`${v.name ?? v.Name ?? ''}: ${v.value ?? v.Value ?? ''}`} />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('stock.addModal.layout.variantLabelsHint', 'Bu bir çeşit ürünü. Çeşit bilgisi ana ürünün varyant yönetiminden değiştirilir.')}
          </Typography>
        </Box>
      )}

      <GroupLabel>{t('stock.addModal.layout.features', 'Özellikler')}</GroupLabel>
      {f.itemFeatureValues.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('stock.addModal.layout.noFeatures', 'Henüz özellik yok. Aşağıdan seçin veya yeni ekleyin.')}
        </Typography>
      )}

      <Box sx={{ display: 'grid', gap: 1 }}>
        {f.itemFeatureValues.map((row) => {
          const selected = f.availableFeatures.find(def => def.id === row.featureId) || null;
          const options = f.availableFeatures.filter(def => !usedIds.has(def.id) || def.id === row.featureId);
          return (
            <Box
              key={row.tempId}
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: 'minmax(0, 2fr) minmax(0, 3fr) auto' }, gap: 1, alignItems: 'center' }}
            >
              <Autocomplete
                size="small"
                options={options}
                value={selected}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => f.handleUpdateFeatureRow(row.tempId, { featureId: value?.id || '' })}
                renderInput={(params) => <TextField {...params} label={t('stock.addModal.fields.extraFeature')} />}
                sx={{ gridColumn: { xs: '1 / 2', sm: 'auto' } }}
              />
              <TextField
                size="small"
                label={t('stock.addModal.fields.extraFeatureValue')}
                value={row.value}
                onChange={(e) => f.handleUpdateFeatureRow(row.tempId, { value: e.target.value })}
                sx={{ gridColumn: { xs: '1 / 2', sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' } }}
              />
              <IconButton
                color="error"
                size="small"
                onClick={() => f.handleRemoveFeatureRow(row.tempId)}
                aria-label={t('stock.addModal.actions.remove')}
                sx={{ gridColumn: { xs: 2, sm: 'auto' }, gridRow: { xs: '1 / 3', sm: 'auto' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {suggestions.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {suggestions.map(name => (
            <Chip
              key={name}
              size="small"
              variant="outlined"
              icon={busyName === name ? <CircularProgress size={12} /> : <AddIcon fontSize="small" />}
              label={name}
              disabled={busyName !== null}
              onClick={() => addSuggested(name)}
            />
          ))}
        </Stack>
      )}

      <Button
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        sx={{ mt: 1, px: 0.5 }}
        onClick={() => addRow()}
        disabled={f.availableFeatures.length === 0}
      >
        {t('stock.addModal.fields.addExtraFeatureRow')}
      </Button>
      {f.availableFeatures.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('stock.addModal.layout.useSuggestions', 'Tanımlı özellik yok; yukarıdaki önerilerden biriyle başlayın.')}
        </Typography>
      )}
      {featureError && <Alert severity="error" sx={{ mt: 1 }}>{featureError}</Alert>}
    </Box>
  );
};
